import { useCallback, useEffect, useMemo, useState } from "react";

// PO-3531: Customer-facing buffer matching `Settings.order_submission_buffer_in_minutes`
// (default 30, source: packages/core/app/lib/order_submission_buffer.rb).
// The backend serializers expose the raw system cutoff; consumers are
// responsible for applying the buffer per the codebase convention used by
// transit_detail.customer_facing_cutoff_time_in_time_zone and the print/transfers ETA calculators.
export const FRAUD_BUFFER_MS = 30 * 60 * 1000;

const TICK_MS = 60_000;

type Subscriber = () => void;

const subscribers = new Set<Subscriber>();
let tickerHandle: ReturnType<typeof setInterval> | null = null;
let visibilityHandler: (() => void) | null = null;

function notifyAll(): void {
  subscribers.forEach(cb => cb());
}

function startTicker(): void {
  if (tickerHandle) return;
  tickerHandle = setInterval(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    notifyAll();
  }, TICK_MS);
  if (typeof document !== "undefined") {
    visibilityHandler = () => {
      if (!document.hidden) notifyAll();
    };
    document.addEventListener("visibilitychange", visibilityHandler);
  }
}

function stopTicker(): void {
  if (tickerHandle) {
    clearInterval(tickerHandle);
    tickerHandle = null;
  }
  if (visibilityHandler && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
}

function subscribe(cb: Subscriber): () => void {
  subscribers.add(cb);
  if (subscribers.size === 1) startTicker();
  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0) stopTicker();
  };
}

// Test-only escape hatch — never call from production code.
export function __resetDeliveryCountdownForTests(): void {
  subscribers.clear();
  stopTicker();
}

export type UseDeliveryCountdownResult = {
  // null when cutoffAt is missing/unparseable; otherwise a non-negative count.
  secondsRemaining: number | null;
  // True the moment the customer-facing cutoff has been reached or passed.
  // Consumers wire this to their refetch action (e.g. shipping/fetchShipments).
  hasElapsed: boolean;
};

export function useDeliveryCountdown(
  cutoffAt: string | null | undefined
): UseDeliveryCountdownResult {
  const customerFacingMs = useMemo(() => {
    if (!cutoffAt) return null;
    const ms = new Date(cutoffAt).getTime() - FRAUD_BUFFER_MS;
    return Number.isFinite(ms) ? ms : null;
  }, [cutoffAt]);

  const compute = useCallback((): number | null => {
    if (customerFacingMs == null) return null;
    return Math.max(0, Math.floor((customerFacingMs - Date.now()) / 1000));
  }, [customerFacingMs]);

  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(compute);

  useEffect(() => {
    setSecondsRemaining(compute());
    const unsubscribe = subscribe(() => setSecondsRemaining(compute()));
    return unsubscribe;
  }, [compute]);

  return {
    secondsRemaining,
    hasElapsed: secondsRemaining === 0
  };
}
